import {
  COULEURS,
  COULEURS_DE_BULLE,
  FORMES_DE_PROJET,
  NOMS_DES_COULEURS_DE_BULLE,
  OBJETS_DE_BULLE,
  TRAITS_DE_PROJET,
  type Couleur,
  type FormeDeBulle,
  type FormeDeProjet,
  type TraitDeProjet,
} from '@nextilt/commun';
import type { ChangementDHabitDUneBulle } from '../partage/api/types';
import { teinteOuAccent } from '../apparence';
import { ApercuDeForme } from '../partage/ApercuDeForme';
import { useHabiller, useHabillerLaBulle } from '../partage/useArbre';
import {
  COMME_LE_PROJET,
  COULEUR_DE_LA_BULLE,
  COULEUR_DU_PROJET_SEULEMENT,
  FAMILLE_DOCUMENTS,
  FAMILLE_GEOMETRIQUE,
  FAMILLE_IDEES,
  FAMILLE_OBJETS,
  FORME_DE_LA_BULLE,
  HABIT_DE_LA_BULLE,
  HABIT_PAS_ECRIT,
  MOTS_DES_FORMES_DE_BULLE,
  MOTS_DES_TRAITS_DE_PROJET,
  TRAIT_DE_LA_BULLE,
} from '../partage/vocabulaire';
import './ChoixEntoures.css';
import './HabitDeLaBulle.css';

/**
 * Les formes de la fiche, rangées par famille (retour d'Igor du 2026-10-21) ; le menu « ⋯ » ne
 * montre que les quinze de décor, le galet et les objets (`DECOR`). Chaque forme est dans une
 * famille et une seule.
 */
const FAMILLES: readonly { nom: string; formes: readonly FormeDeBulle[] }[] = [
  { nom: FAMILLE_GEOMETRIQUE, formes: FORMES_DE_PROJET },
  { nom: FAMILLE_DOCUMENTS, formes: ['dossier', 'fichier', 'enveloppe', 'calendrier', 'billet'] },
  { nom: FAMILLE_IDEES, formes: ['ampoule', 'nuage', 'horloge', 'drapeau'] },
  { nom: FAMILLE_OBJETS, formes: ['telephone', 'ordinateur', 'panier', 'outils', 'maison'] },
];
const DECOR: readonly FormeDeBulle[] = ['galet', ...OBJETS_DE_BULLE];

/** Ce que l'habit d'une bulle lit d'elle : sa mère, et sa couleur, sa forme et son trait à elle. */
export interface BulleAVetir {
  id: string;
  parentId: string | null;
  couleur?: string | null;
  forme?: FormeDeBulle | null;
  trait?: TraitDeProjet | null;
}

/** Ce que l'habit d'une bulle lit du projet : sa couleur, la forme et le trait de ses bulles. */
export interface HabitDuProjetPourUneBulle {
  couleur: string | null;
  forme: FormeDeProjet;
  trait: TraitDeProjet;
}

/**
 * **L'habit d'une bulle** (chantier habit-des-bulles, modification 04), sur le modèle du bas de la
 * fiche punaisée du thème Réflexion (`.scratch/chantiers/themes/maquettes/quatre-themes.html`,
 * `.fr-reg`) : la grille des formes dessinées en petit, celle de la bulle surlignée (`.fr-formes`
 * `.ici`), rangées par famille, et dans le menu « ⋯ » les quinze de décor ; une rangée de
 * pastilles rondes, les douze couleurs d'une branche (`.fr-papiers`) ; une rangée de traits, chacun
 * en ligne. Chaque rangée commence par « Comme le projet » (`null` : la bulle revient à l'habit du
 * projet, et, pour la couleur, à celle de sa mère, sinon à l'automatique). Un clic change la bulle
 * aussitôt, et s'enregistre (`useHabillerLaBulle`).
 *
 * Sur la bulle du projet : sa couleur seulement, celle du projet (`useHabiller`) ; sa forme et son
 * trait se règlent dans l'Habit du projet. Les mêmes rangées dans la fiche (un espace à part) et
 * dans le menu « ⋯ » d'une bulle (05, `compact`).
 */
export function HabitDeLaBulle({
  bulle,
  projet,
  compact = false,
  enLignes = false,
}: {
  bulle: BulleAVetir;
  projet: HabitDuProjetPourUneBulle;
  /** Dans le menu « ⋯ » de la carte : sans titre, en plus petit. */
  compact?: boolean;
  /**
   * Dans l'intercalaire Visuel des Réglages de la fiche (reglages-de-la-fiche, deuxième passe de
   * 02) : sans titre, une ligne par rangée, le nom à gauche ; « Comme le projet » et les traits en
   * mots entourés au crayon (`ChoixEntoures.css`), les formes et les couleurs dessinées.
   */
  enLignes?: boolean;
}) {
  const habillerLaBulle = useHabillerLaBulle();
  const habillerLeProjet = useHabiller();
  const duProjet = bulle.parentId === null;
  const choisir = (changement: ChangementDHabitDUneBulle) => habillerLaBulle.mutate({ id: bulle.id, changement });

  const couleur = duProjet ? projet.couleur : (bulle.couleur ?? null);
  const forme = bulle.forme ?? null;
  const trait = bulle.trait ?? null;
  /* Les aperçus des formes portent le trait de la bulle, sinon celui du projet. */
  const traitMontre = trait ?? projet.trait;
  const pasEcrit = habillerLaBulle.isError || habillerLeProjet.isError;
  /* Un projet garde sa couleur parmi les cinq ; une branche choisit parmi les douze. */
  const couleurs = duProjet ? COULEURS : COULEURS_DE_BULLE;
  const classeDuComme = enLignes ? 'choix-entoure' : 'habit-bulle-comme';

  const boutonDeForme = (f: FormeDeBulle) => (
    <button
      key={f}
      type="button"
      role="radio"
      aria-checked={forme === f}
      aria-label={MOTS_DES_FORMES_DE_BULLE[f]}
      title={MOTS_DES_FORMES_DE_BULLE[f]}
      data-valeur={f}
      className="habit-bulle-forme"
      onClick={() => {
        if (forme !== f) choisir({ forme: f });
      }}
    >
      <ApercuDeForme forme={f} trait={traitMontre} petit />
    </button>
  );

  return (
    <section
      className={`habit-bulle${compact ? ' habit-bulle--compact' : ''}${enLignes ? ' habit-bulle--en-lignes' : ''}`}
      aria-label={HABIT_DE_LA_BULLE}
      data-habit-bulle={bulle.id}
    >
      {!compact && !enLignes && <h3 className="habit-bulle-titre">{HABIT_DE_LA_BULLE}</h3>}

      {!duProjet && (
        <div className="habit-bulle-rangee" role="radiogroup" aria-label={FORME_DE_LA_BULLE} data-habit="forme">
          <span className="habit-bulle-nom">{FORME_DE_LA_BULLE}</span>
          <div className="habit-bulle-choix">
            <button
              type="button"
              role="radio"
              aria-checked={forme === null}
              className={classeDuComme}
              onClick={() => {
                if (forme !== null) choisir({ forme: null });
              }}
            >
              {COMME_LE_PROJET}
            </button>
            {compact ? (
              <div className="habit-bulle-formes">{DECOR.map(boutonDeForme)}</div>
            ) : (
              FAMILLES.map((famille) => (
                <div key={famille.nom} className="habit-bulle-famille">
                  <span className="habit-bulle-famille-nom">{famille.nom}</span>
                  <div className="habit-bulle-formes">{famille.formes.map(boutonDeForme)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="habit-bulle-rangee" role="radiogroup" aria-label={COULEUR_DE_LA_BULLE} data-habit="couleur">
        <span className="habit-bulle-nom">{COULEUR_DE_LA_BULLE}</span>
        <div className="habit-bulle-couleurs">
          {!duProjet && (
            <button
              type="button"
              role="radio"
              aria-checked={couleur === null}
              className={classeDuComme}
              onClick={() => {
                if (couleur !== null) choisir({ couleur: null });
              }}
            >
              {COMME_LE_PROJET}
            </button>
          )}
          {couleurs.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={couleur === c}
              aria-label={NOMS_DES_COULEURS_DE_BULLE[c]}
              title={NOMS_DES_COULEURS_DE_BULLE[c]}
              data-valeur={c}
              className="habit-bulle-couleur"
              style={{ ['--pastille' as string]: teinteOuAccent(c) }}
              onClick={() => {
                if (couleur === c) return;
                if (duProjet) habillerLeProjet.mutate({ projetId: bulle.id, changement: { couleur: c as Couleur } });
                else choisir({ couleur: c });
              }}
            />
          ))}
        </div>
      </div>

      {!duProjet && (
        <div className="habit-bulle-rangee" role="radiogroup" aria-label={TRAIT_DE_LA_BULLE} data-habit="trait">
          <span className="habit-bulle-nom">{TRAIT_DE_LA_BULLE}</span>
          <div className="habit-bulle-traits">
            <button
              type="button"
              role="radio"
              aria-checked={trait === null}
              className={classeDuComme}
              onClick={() => {
                if (trait !== null) choisir({ trait: null });
              }}
            >
              {COMME_LE_PROJET}
            </button>
            {TRAITS_DE_PROJET.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={trait === t}
                aria-label={MOTS_DES_TRAITS_DE_PROJET[t]}
                title={MOTS_DES_TRAITS_DE_PROJET[t]}
                data-valeur={t}
                className={enLignes ? 'choix-entoure' : 'habit-bulle-trait'}
                onClick={() => {
                  if (trait !== t) choisir({ trait: t });
                }}
              >
                {/* Une ligne au trait, pas la forme : en petit, la forme cachait la différence. Dans les Réglages : le mot seul, entouré. */}
                {!enLignes && <span className="habit-bulle-ligne trait-de-projet" data-trait={t} aria-hidden="true" />}
                {!compact && MOTS_DES_TRAITS_DE_PROJET[t]}
              </button>
            ))}
          </div>
        </div>
      )}

      {duProjet && !compact && <p className="habit-bulle-note">{COULEUR_DU_PROJET_SEULEMENT}</p>}

      {pasEcrit && (
        <p className="habit-bulle-refus" role="status">
          {HABIT_PAS_ECRIT}
        </p>
      )}
    </section>
  );
}
